/**
 * Pure helpers for rendering #361 formCheck blobs in the display app (#362).
 * Missing / unrecognised formCheck is "no signal", never "ok".
 */

export const FORM_CHECK_VERSION = 1;

export const FORM_CHECK_PENDING_TIMEOUT_MS = 20000;

/**
 * Every form-check notification — the dot on a map card, its outline, the pill
 * and the note under a field — wears this amber, blocking or not. Red read as
 * "stop" to the room (#1431). White does not read on it, so marks use the ink.
 */
export const FORM_CHECK_COLOR = '#F2B544';
export const FORM_CHECK_INK = '#1a1a1a';

export const FORM_CHECK_STAGES = {
  IMPACT_PATH_NODE: 'impact_path_node',
  LENS_ITEM: 'lens_item',
  SOLUTION: 'solution',
  SYSTEMIC_MAP_NODE: 'systemic_map_node',
};

export const FORM_CHECK_ISSUE_CODES = [
  'too_short',
  'garbled',
  'test_data',
  'off_topic',
  'wrong_stage',
  'multiple_ideas',
];

const KNOWN_CODES = new Set(FORM_CHECK_ISSUE_CODES);

/**
 * Codes that stop the record's AI analysis — a mirror of the backend's
 * ANALYSIS_BLOCKING_ISSUE_CODES (apps/backend/src/shared/analysisEligibility.js).
 * The other codes are advice the writer may ignore; the UI shows them quietly.
 */
export const FORM_CHECK_BLOCKING_CODES = ['too_short', 'garbled', 'test_data'];

const BLOCKING_CODES = new Set(FORM_CHECK_BLOCKING_CODES);

/** @returns {'blocking' | 'advisory'} the louder of the issues' two severities */
export function issuesSeverity(issues = []) {
  return (Array.isArray(issues) ? issues : []).some((issue) => issue && BLOCKING_CODES.has(issue.code))
    ? 'blocking'
    : 'advisory';
}

/** @returns {'blocking' | 'advisory' | null} null when the blob carries no warning */
export function formCheckSeverity(formCheck) {
  if (resolveFormCheckState({ formCheck }) !== 'warn') return null;
  return issuesSeverity(formCheck.issues);
}

export const FORM_CHECK_BLOB_KEY = {
  [FORM_CHECK_STAGES.SOLUTION]: 'ai_analysis',
  [FORM_CHECK_STAGES.LENS_ITEM]: 'rag_references',
  [FORM_CHECK_STAGES.IMPACT_PATH_NODE]: 'node_analysis',
  [FORM_CHECK_STAGES.SYSTEMIC_MAP_NODE]: 'node_analysis',
};

/**
 * @param {{ formCheck?: object|null, pending?: boolean }} args
 * @returns {'pending' | 'ok' | 'warn' | 'none'}
 */
export function resolveFormCheckState({ formCheck, pending } = {}) {
  if (formCheck && formCheck.version === FORM_CHECK_VERSION) {
    if (formCheck.status === 'warn') {
      const issues = Array.isArray(formCheck.issues) ? formCheck.issues : [];
      return issues.length > 0 ? 'warn' : 'ok';
    }
    if (formCheck.status === 'ok') return 'ok';
    return 'none';
  }
  if (pending) return 'pending';
  return 'none';
}

/**
 * Server message first, locale fallback by code, unknown-and-empty dropped.
 * @param {object|null|undefined} formCheck
 * @param {(key: string) => string} t
 * @returns {{ code: string, message: string }[]}
 */
export function formCheckIssueMessages(formCheck, t) {
  const issues = Array.isArray(formCheck?.issues) ? formCheck.issues : [];
  const out = [];
  for (const issue of issues) {
    if (!issue || typeof issue !== 'object') continue;
    const code = String(issue.code || '');
    const serverMessage = String(issue.message || '').trim();
    if (serverMessage) {
      out.push({ code, message: serverMessage });
      continue;
    }
    if (KNOWN_CODES.has(code) && typeof t === 'function') {
      const fallback = t(`formCheck.issues.${code}`);
      if (fallback && fallback !== `formCheck.issues.${code}`) {
        out.push({ code, message: fallback });
      }
    }
  }
  return out;
}

export function shouldAcceptFormCheck(held, incoming, ignoreBeforeMs) {
  if (!incoming || incoming.version !== FORM_CHECK_VERSION) return false;
  if (ignoreBeforeMs && incoming.checkedAt) {
    const incomingMs = Date.parse(incoming.checkedAt);
    if (!Number.isNaN(incomingMs) && incomingMs <= ignoreBeforeMs) return false;
  }
  if (held?.checkedAt && incoming.checkedAt && held.checkedAt >= incoming.checkedAt) {
    return false;
  }
  return true;
}

export function extractFormCheck(record, stage) {
  if (!record) return null;
  const key = FORM_CHECK_BLOB_KEY[stage];
  return key ? record[key]?.formCheck || null : null;
}

export function patchRecordFormCheck(records, recordId, formCheck, blobKey) {
  let found = false;
  const next = records.map((record) => {
    if (String(record.id) !== String(recordId)) return record;
    found = true;
    const blob = record[blobKey];
    if (!shouldAcceptFormCheck(blob?.formCheck, formCheck)) return record;
    return { ...record, [blobKey]: { ...(blob || {}), formCheck } };
  });
  return { records: found ? next : records, found };
}

export function stripRecordFormCheck(record, blobKey) {
  const blob = record?.[blobKey];
  if (!blob?.formCheck) return record;
  const { formCheck, ...rest } = blob;
  return { ...record, [blobKey]: rest };
}
